import React from "react";
import { Col, Row } from 'react-bootstrap';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Langs from '../../app/lang/langs';
import { config } from '../../app/utils/config';
import styles from './Permissions.module.css';
import PermissionButton from './PermissionButton';
import PermissionScopeTooltip from './PermissionScopeTooltip';

export default function PermissionRow({ scope, scopeType, role, activePermissions, onPermissionChange }) {

    const isGlobal = scopeType === "global";
    const allowTeamRemove = !isGlobal && (scope === "evaluations" || scope === "ventr" || scope === "warnings");
    // Scopes every collab already reaches without a permission row. "Remove" would be a lie
    // for these — revoking the row does not take the module away — so only View/Edit are offered
    // and View shows as the active floor.
    const disallowGlobalRemove = isGlobal && (scope === "news" || scope === "vsecurite" || scope === "vreference");
    // `vlearn` is team-bound but deliberately offers only three of the four tiers. There is no
    // View all: a read-only window onto every course in the company answers no question anyone
    // asked, while Edit all — author for any team — is a real role. And "Remove" would be a lie
    // here for the same reason it is for the scopes above: every collab already takes the
    // courses assigned to them with no permission row at all, so revoking one does not take
    // V-Learning away. View is therefore the active floor.
    const isThreeTierTeamScope = !isGlobal && scope === "vlearn";
    const permissionTypes = isGlobal
        ? [
            ...(disallowGlobalRemove ? [] : [config.permissionTypes.remove]),
            config.permissionTypes.view,
            config.permissionTypes.edit,
        ]
        : isThreeTierTeamScope
            ? [
                config.permissionTypes.view,
                config.permissionTypes.edit,
                config.permissionTypes.edit_all,
            ]
            : [
                ...(allowTeamRemove ? [config.permissionTypes.remove] : []),
                config.permissionTypes.view,
                config.permissionTypes.edit,
                config.permissionTypes.view_all,
                config.permissionTypes.edit_all,
            ];

    const labelColProps = isGlobal
        ? { lg: 4, xl: 4, xxl: 4 }
        : { lg: 4, xl: 4, xxl: 4 };
    const buttonColProps = isGlobal
        ? { lg: 8, xl: 8, xxl: 8 }
        : { lg: 8, xl: 8, xxl: 8 };

    // For scopes where view is the implicit floor (no "Remove" option),
    // show View as active by default when no other tier is selected.
    const editActive = !!activePermissions[config.permissionTypes.edit]?.includes(scope)
    const viewActive = !!activePermissions[config.permissionTypes.view]?.includes(scope)
    // A team-bound floor scope also has the "all teams" tiers to account for: without this,
    // a role holding `vlearn:edit_all` would light up Edit all AND View, because neither of
    // the two flags above is set by an `_all` grant.
    const anyTierActive = editActive || viewActive
        || !!activePermissions[config.permissionTypes.edit_all]?.includes(scope)
        || !!activePermissions[config.permissionTypes.view_all]?.includes(scope)

    const isTypeActive = (type) => {
        if (type === config.permissionTypes.view
            && (disallowGlobalRemove || isThreeTierTeamScope)
            && !anyTierActive) {
            return true
        }
        return !!activePermissions[type]?.includes(scope)
    }

    return (
        <Row className={styles.permissionRow}>
            <Col {...labelColProps} className={isGlobal ? styles.labelGlobal : styles.labelTeam}>
                <PermissionScopeTooltip scope={scope} scopeType={scopeType}>
                    <span className={styles.scopeLabel} tabIndex={0}>
                        <Langs str={scope} />
                        <InfoOutlinedIcon className={styles.scopeInfoIcon} />
                    </span>
                </PermissionScopeTooltip>
            </Col>
            <Col {...buttonColProps} className={styles.buttonGroup}>
                {permissionTypes.map(type => (
                    <PermissionButton
                        key={type}
                        type={type}
                        isActive={isTypeActive(type)}
                        onClick={() => onPermissionChange(role, scopeType, scope, type)}
                        useLocalize={!isGlobal && type !== config.permissionTypes.remove}
                    />
                ))}
            </Col>
        </Row>
    );
}
